import PropTypes from "prop-types"
import React, { useRef, useState } from "react"
import { useQuery } from "react-query"
import WorkOrderStatusBadge from "src/main/Service/Manage/WorkOrders/WorkOrderStatusBadge"

import Button from "src/components/Button"
import Pagination from "src/components/Pagination"
import Table from "src/components/Table"

import { queryContactWorkOrders } from "src/api/Contacts"

const ContactWorkOrders = ({ contactId, initialData, labels }) => {
  const [page, setPage] = useState(1)

  const { data, isError, refetch } = useQuery(
    ["contactWorkOrders", contactId, page],
    () => queryContactWorkOrders(contactId, page),
    {
      initialData: page === 1 ? initialData : undefined,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: Infinity,
    }
  )

  // keepPreviousData drops the previous page once a fetch errors, so hold the
  // last loaded page ourselves to keep its rows on screen under the alert.
  const lastLoaded = useRef(initialData)
  if (data) {
    lastLoaded.current = data
  }

  const workOrders = lastLoaded.current.items
  const pageCount = lastLoaded.current.pageCount

  return (
    <div
      data-testid="contact-work-orders-table"
      className="h-full w-max px-4 md:w-full"
    >
      {isError && (
        <div role="alert" className="flex items-center gap-2 py-4 text-red-600">
          <span>{labels.loadError}</span>
          <Button small variant="ghost" onClick={() => refetch()}>
            {labels.retry}
          </Button>
        </div>
      )}
      <Table fullHeight autoColumnWidth>
        <Table.Head>
          <Table.Head.Row>
            <Table.Head.Cell>{labels.workOrder}</Table.Head.Cell>
            <Table.Head.Cell>{labels.boat}</Table.Head.Cell>
            <Table.Head.Cell>{labels.status}</Table.Head.Cell>
          </Table.Head.Row>
        </Table.Head>
        <Table.Body>
          {workOrders.map((workOrder) => (
            <Table.Row
              key={workOrder.path}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey) {
                  window.open(workOrder.path, "_blank")
                } else {
                  window.location.assign(workOrder.path)
                }
              }}
            >
              <Table.Cell>
                <a
                  href={workOrder.path}
                  onClick={(event) => event.stopPropagation()}
                >
                  {workOrder.displaySequentialId}
                </a>
              </Table.Cell>
              <Table.Cell>{workOrder.boatName}</Table.Cell>
              <Table.Cell>
                <WorkOrderStatusBadge
                  status={workOrder.status}
                  statusLabel={workOrder.statusLabel}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {pageCount > 1 && (
        <Pagination
          page={page}
          numberOfPages={pageCount}
          onPageChange={({ selected }) => setPage(selected)}
        />
      )}
    </div>
  )
}

ContactWorkOrders.propTypes = {
  contactId: PropTypes.string.isRequired,
  initialData: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        displaySequentialId: PropTypes.string.isRequired,
        boatName: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        statusLabel: PropTypes.string.isRequired,
        path: PropTypes.string.isRequired,
      })
    ).isRequired,
    pageCount: PropTypes.number.isRequired,
  }).isRequired,
  labels: PropTypes.shape({
    workOrder: PropTypes.string.isRequired,
    loadError: PropTypes.string.isRequired,
    retry: PropTypes.string.isRequired,
    boat: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
}

export default ContactWorkOrders
